import React, { PropsWithChildren, ReactElement } from "react";

import { IconButton } from "@mui/material";
import MuiTableRow from "@mui/material/TableRow";
import { FormProvider, useForm, UseFormProps } from "react-hook-form";

import { FartherTableBodyCell } from "../Components";
import CircleCheck from "@src/assets/svg/components/CircleCheck";
import CircleX from "@src/assets/svg/components/CircleX";
import TrashCan from "@src/assets/svg/components/trashCan";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";

import styles from "./FormTableRow.module.css";

interface TableRowProps<TFormValues extends Record<string, TFormValues>> {
  formName: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
  onSubmit: (formName: string, values: TFormValues) => void;
  onDeleteRow: (formName: string) => void;
  defaultValues?: UseFormProps<TFormValues>["defaultValues"];
  children:
    | ReactElement<typeof FartherTableBodyCell>[]
    | ReactElement<typeof FartherTableBodyCell>;
}

const FormTableRow = <TFormValues extends Record<string, TFormValues>>({
  children,
  className,
  onClick,
  onSubmit,
  onDeleteRow,
  defaultValues,
  formName,
}: PropsWithChildren<TableRowProps<TFormValues>>): JSX.Element => {
  const methods = useForm<TFormValues>({ defaultValues });

  const { formState, reset } = methods;

  const handleOnSubmit = () => {
    onSubmit(formName, methods.getValues());
    reset(methods.getValues());
  };

  const handleCancel = () => {
    reset();
  };

  const handleOnDeleteRow = () => {
    onDeleteRow(formName);
  };

  return (
    <FormProvider {...methods}>
      <MuiTableRow
        onClick={onClick}
        className={toClassName(styles.row, className)}
      >
        {children}
        <FartherTableBodyCell
          className={toClassName(styles.tableBodyCell, styles.stickyHeaderCell)}
          style={{
            textAlign: "center",
            position: "relative",
          }}
        >
          <div className={styles.actionContainer}>
            <div
              className={toClassName(styles.formActions, {
                [styles.visible]: !formState.isDirty,
                [styles.onTop]: !formState.isDirty,
              })}
            >
              <IconButton onClick={handleOnDeleteRow}>
                <TrashCan />
              </IconButton>
            </div>

            <div
              className={toClassName(styles.formActions, {
                [styles.visible]: formState.isDirty,
                [styles.onTop]: formState.isDirty,
              })}
            >
              <IconButton onClick={handleCancel}>
                <CircleX />
              </IconButton>
              <IconButton onClick={handleOnSubmit}>
                <CircleCheck />
              </IconButton>
            </div>
          </div>
        </FartherTableBodyCell>
      </MuiTableRow>
    </FormProvider>
  );
};

FormTableRow.displayName = "FormTableRow";

export default FormTableRow;
